import React, { useMemo, useEffect } from 'react';
import { useTabCompletion } from '../../hooks/useTabCompletion';
import { GhostInput } from '../../components/common/GhostInput';
import { UPGRADES } from '../../data/upgrades';
import UpgradeNode from './UpgradeNode';
import { useStore } from '../../store/useStore';

// Define tabs - no longer need prefixes since we use category field
const TABS = [
    { id: 'mining', label: 'Mining' },
    { id: 'farming', label: 'Farming' },
    { id: 'economy', label: 'Economy' },
    { id: 'crafting', label: 'Crafting' },
] as const;

type TabId = typeof TABS[number]['id'];


const INITIAL_TRANSFORM = { x: 0, y: 150, scale: 1 };

const TechTree = () => {
    const { upgrades } = useStore();

    // Tab State
    const [activeTab, setActiveTab] = React.useState<TabId>('mining');

    // Pan/Zoom State
    const [transform, setTransform] = React.useState(INITIAL_TRANSFORM);
    const [isDragging, setIsDragging] = React.useState(false);
    const [lastMouse, setLastMouse] = React.useState({ x: 0, y: 0 });
    const [hoveredNodeId, setHoveredNodeId] = React.useState<string | null>(null);

    // Search State
    const [searchQuery, setSearchQuery] = React.useState('');
    const [matchingNodes, setMatchingNodes] = React.useState<string[]>([]);

    // Filter upgrades by active tab using category field
    const filteredUpgrades = useMemo(() => {
        return UPGRADES.filter(u => u.category === activeTab);
    }, [activeTab]);

    // Reset view when tab changes
    useEffect(() => {
        setTransform(INITIAL_TRANSFORM);
        setSearchQuery('');
        setMatchingNodes([]);
    }, [activeTab]);

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const scaleAmount = -e.deltaY * 0.001;
        const newScale = Math.min(Math.max(0.5, transform.scale * (1 + scaleAmount)), 3);

        // Zoom towards mouse? Complex. Let's just zoom center for now or simple scale.
        // Simple scale update:
        setTransform(prev => ({ ...prev, scale: newScale }));
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setLastMouse({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        const dx = e.clientX - lastMouse.x;
        const dy = e.clientY - lastMouse.y;
        setTransform(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
        setLastMouse({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    // Auto-Layout Algorithm
    const { bounds, nodePositions } = useMemo(() => {
        // 1. Assign Depths (X-Axis) - use filteredUpgrades
        const depthMap = new Map<string, number>();
        const getDepth = (id: string): number => {
            if (depthMap.has(id)) return depthMap.get(id)!;
            const u = filteredUpgrades.find(n => n.id === id);
            if (!u || !u.prerequisites || u.prerequisites.length === 0) {
                depthMap.set(id, 0);
                return 0;
            }
            // Check if all prerequisites are in the filtered set
            const validPrereqs = u.prerequisites.filter(p =>
                filteredUpgrades.some(fu => fu.id === p)
            );
            if (validPrereqs.length === 0) {
                depthMap.set(id, 0);
                return 0;
            }
            // Max depth of parents + 1
            const parentDepths = validPrereqs.map(getDepth);
            const d = Math.max(...parentDepths) + 1;
            depthMap.set(id, d);
            return d;
        };
        filteredUpgrades.forEach(u => getDepth(u.id));

        // 2. Identify Relationships (Parents/Children)
        const allPrereqs = new Set<string>();
        filteredUpgrades.forEach(u => {
            u.prerequisites?.forEach(p => {
                if (filteredUpgrades.some(fu => fu.id === p)) {
                    allPrereqs.add(p);
                }
            });
        });

        const isConnected = (u: typeof UPGRADES[0]) => {
            const hasParents = u.prerequisites?.some(p =>
                filteredUpgrades.some(fu => fu.id === p)
            );
            const isParent = allPrereqs.has(u.id);
            return hasParents || isParent;
        };

        // 3. Layout all nodes (no clustering needed - each tab is a category)
        const positions = new Map<string, { x: number, y: number }>();
        const ROW_HEIGHT = 120;
        const COL_WIDTH = 200;
        const START_X = 100;
        const START_Y = 100;

        // Split into Tree (Connected) and Grid (Isolated)
        const treeNodes = filteredUpgrades.filter(u => isConnected(u));
        const gridNodes = filteredUpgrades.filter(u => !isConnected(u));

        // --- LAYOUT TREE NODES ---
        const depthGroups: Record<number, typeof UPGRADES> = {};
        let maxDepth = 0;
        treeNodes.forEach(u => {
            const d = depthMap.get(u.id) ?? 0;
            if (!depthGroups[d]) depthGroups[d] = [];
            depthGroups[d]!.push(u);
            if (d > maxDepth) maxDepth = d;
        });

        // Helper for alignment
        const getIdealY = (u: typeof UPGRADES[0]) => {
            const validPrereqs = u.prerequisites?.filter(p =>
                filteredUpgrades.some(fu => fu.id === p)
            ) || [];
            if (validPrereqs.length === 0) return -1;
            let sum = 0;
            let count = 0;
            validPrereqs.forEach(pid => {
                const parentPos = positions.get(pid);
                if (parentPos) {
                    sum += parentPos.y;
                    count++;
                }
            });
            return count > 0 ? sum / count : -1;
        };

        let maxYInTree = START_Y;
        if (treeNodes.length > 0) {
            // Iterate layers
            for (let d = 0; d <= maxDepth; d++) {
                const nodes = depthGroups[d] || [];

                // Sort by Ideal Y to align with parents
                nodes.sort((a, b) => {
                    const idealA = getIdealY(a);
                    const idealB = getIdealY(b);
                    if (Math.abs(idealA - idealB) < 5) return a.label.localeCompare(b.label);
                    return idealA - idealB;
                });

                let currentColumnY = START_Y;
                nodes.forEach((u) => {
                    const idealY = getIdealY(u);
                    let y = currentColumnY;

                    if (idealY !== -1) {
                        y = Math.max(currentColumnY, idealY);
                    }

                    const x = START_X + (d * COL_WIDTH);
                    positions.set(u.id, { x, y });
                    if (y > maxYInTree) maxYInTree = y;
                    currentColumnY = y + ROW_HEIGHT;
                });
            }
        }

        // --- LAYOUT GRID NODES ---
        let gridStartY = treeNodes.length > 0 ? (maxYInTree + ROW_HEIGHT) : START_Y;

        if (gridNodes.length > 0) {
            const ITEMS_PER_ROW = 6;
            gridNodes.forEach((u, idx) => {
                const col = idx % ITEMS_PER_ROW;
                const row = Math.floor(idx / ITEMS_PER_ROW);

                const x = START_X + (col * COL_WIDTH);
                const y = gridStartY + (row * ROW_HEIGHT);

                positions.set(u.id, { x, y });
                if (y > maxYInTree) maxYInTree = y;
            });
        }

        // Bounds calculation
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        positions.forEach(p => {
            if (p.x < minX) minX = p.x;
            if (p.x > maxX) maxX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.y > maxY) maxY = p.y;
        });

        return {
            nodePositions: positions,
            bounds: {
                width: (maxX - minX) + 600,
                height: (maxY - minY) + 600,
                offsetX: -minX + 100,
                offsetY: -minY + 100
            }
        };
    }, [filteredUpgrades]);

    const containerRef = React.useRef<HTMLDivElement>(null);

    // Scroll to Node
    const scrollToNode = React.useCallback((nodeId: string) => {
        const pos = nodePositions.get(nodeId);
        if (!pos || !containerRef.current) return;

        const { clientWidth, clientHeight } = containerRef.current;

        const centerX = clientWidth / 2;
        const centerY = clientHeight / 2;

        setTransform(prev => ({
            ...prev,
            x: centerX - (pos.x * prev.scale),
            y: centerY - (pos.y * prev.scale)
        }));
    }, [nodePositions]);

    // Handle Search
    // Handle Search Logic via Effect
    useEffect(() => {
        if (!searchQuery) {
            setMatchingNodes([]);
            return;
        }

        const lowerQuery = searchQuery.toLowerCase();
        const matches = filteredUpgrades.filter(u =>
            u.label.toLowerCase().includes(lowerQuery) ||
            u.description.toLowerCase().includes(lowerQuery)
        ).map(u => u.id);

        setMatchingNodes(matches);

        // Auto-pan to first match
        const firstMatch = matches[0];
        if (firstMatch) {
            scrollToNode(firstMatch);
        }
    }, [searchQuery, scrollToNode, filteredUpgrades]);

    const upgradeNames = useMemo(() => filteredUpgrades.map(u => u.label), [filteredUpgrades]);
    const { handleKeyDown: handleTabKeyDown, handleInputChange, hint } = useTabCompletion(searchQuery, setSearchQuery, upgradeNames, 'includes');

    const handleSearchInput = (value: string) => {
        setSearchQuery(value);
    };

    // Generate Connections (SVG Paths)
    const connections = useMemo(() => {
        const paths = [];
        for (const upgrade of filteredUpgrades) {
            if (!upgrade.prerequisites) continue;

            for (const preId of upgrade.prerequisites) {
                // Only show connections to nodes in the current filtered set
                const parent = filteredUpgrades.find(u => u.id === preId);
                if (!parent) continue;

                const isMePurchased = upgrades[upgrade.id];
                const isMyVisible = isMePurchased || (upgrade.prerequisites && upgrade.prerequisites.some(pid => upgrades[pid])) || (!upgrade.prerequisites || upgrade.prerequisites.length === 0);

                // Coordinates from Auto-Layout
                const startPos = nodePositions.get(parent.id);
                const endPos = nodePositions.get(upgrade.id);

                if (!startPos || !endPos) continue;

                const startX = startPos.x;
                const startY = startPos.y;
                const endX = endPos.x;
                const endPosY = endPos.y;

                // Path Logic (Basic Bezier)
                const pathData = `M ${startX} ${startY} L ${endX} ${endPosY}`;

                let stroke = '#333';
                let strokeWidth = 2;
                let strokeDash = 'none';
                let opacity = 1;

                if (isMePurchased) {
                    stroke = '#4caf50'; // Full Green connection
                    strokeWidth = 3;
                } else if (isMyVisible) {
                    stroke = '#666'; // Connected but not bought
                } else {
                    // Undiscovered connection
                    stroke = '#666'; // Increased visibility
                    strokeDash = '5,5';
                    opacity = 0.6;
                }

                paths.push(
                    <path
                        key={`${preId}-${upgrade.id}`}
                        d={pathData}
                        stroke={stroke}
                        strokeWidth={strokeWidth}
                        strokeDasharray={strokeDash}
                        opacity={opacity}
                        fill="none"
                        style={{ transition: 'stroke 0.3s' }}
                    />
                );
            }
        }
        return paths;
    }, [upgrades, nodePositions, filteredUpgrades]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
            {/* Tab Selector */}
            <div style={{
                display: 'flex',
                gap: 4,
                padding: '10px 20px',
                background: '#1a1a1a',
                borderBottom: '1px solid #333',
            }}>
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            padding: '8px 20px',
                            background: activeTab === tab.id ? '#333' : 'transparent',
                            color: activeTab === tab.id ? '#fff' : '#888',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: activeTab === tab.id ? 600 : 400,
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Main Tree Canvas */}
            <div
                ref={containerRef}
                style={{
                    flex: 1,
                    overflow: 'hidden',
                    background: '#111',
                    position: 'relative',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none'
                }}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            >
                {/* Transform Container */}
                <div style={{
                    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                    transformOrigin: 'center',
                    width: '100%', height: '100%',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                }}>
                    {/* Content Wrapper */}
                    <div style={{
                        position: 'absolute',
                        left: 0, top: 0,
                    }}>
                        {/* SVG Layer */}
                        <svg style={{
                            position: 'absolute',
                            overflow: 'visible',
                            top: 0, left: 0,
                            pointerEvents: 'none', zIndex: 0
                        }}>
                            {connections}
                        </svg>

                        {/* Node Layer */}
                        {filteredUpgrades.map(u => {
                            const pos = nodePositions.get(u.id);
                            if (!pos) return null;

                            const isMatch = matchingNodes.includes(u.id);

                            return (
                                <div key={u.id} style={{
                                    position: 'absolute',
                                    left: pos.x,
                                    top: pos.y,
                                    width: 0, height: 0,
                                    zIndex: hoveredNodeId === u.id ? 100 : 1
                                }}>
                                    <UpgradeNode
                                        upgradeId={u.id}
                                        onHoverChange={(isHovered) => setHoveredNodeId(isHovered ? u.id : null)}
                                        isHighlighted={isMatch}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Controls UI overlay */}
                <div style={{ position: 'absolute', bottom: 20, right: 20, display: 'flex', gap: 10, zIndex: 100 }}>

                    {/* Search Box */}
                    <div style={{ position: 'relative', marginRight: 20 }}>
                        <GhostInput
                            placeholder="Search upgrades..."
                            value={searchQuery}
                            hint={hint}
                            onChange={handleInputChange} // This calls setInput and updateHint
                            style={{
                                padding: '5px 10px',
                                background: '#222',
                                color: '#fff',
                                border: '1px solid #555',
                                borderRadius: 4,
                                width: 200
                            }}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                handleTabKeyDown(e);
                            }}
                        />
                    </div>

                    <button onClick={() => setTransform(t => ({ ...t, scale: Math.min(t.scale + 0.2, 3) }))} style={btnStyle}>+</button>
                    <button onClick={() => setTransform(t => ({ ...t, scale: Math.max(t.scale - 0.2, 0.5) }))} style={btnStyle}>-</button>
                    <button onClick={() => setTransform(INITIAL_TRANSFORM)} style={btnStyle}>Reset</button>
                </div>
            </div>
        </div>
    );
};

const btnStyle = {
    padding: '5px 10px',
    background: '#333',
    color: '#fff',
    border: '1px solid #555',
    borderRadius: 4,
    cursor: 'pointer'
};

export default TechTree;
